import React, { ReactNode, createContext, useCallback, useMemo, useState } from 'react';

import { Dialog, Drawer } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import useResponsive from '../../hooks/useResponsive';

export interface ModalContentProps {
    onClose: () => void;
    title?: string;
    [key: string]: unknown;
}

export type ModalContextValuesType = {
    openModal: <T extends ModalContentProps = ModalContentProps>(options: {
        content: React.ComponentType<T>;
        props?: Record<string, unknown>;
        fullScreenOnMobile?: boolean;
    }) => void;
    closeModal: () => void;
    isOpen: boolean;
};

export const ModalContext = createContext<ModalContextValuesType>({
    openModal: <T extends ModalContentProps = ModalContentProps>(_options: {
        content: React.ComponentType<T>;
        props?: Record<string, unknown>;
        fullScreenOnMobile?: boolean;
    }) => {},
    closeModal: () => {},
    isOpen: false,
});

interface ModalManagerProps {
    children: ReactNode;
}

const ModalManager = ({ children }: ModalManagerProps) => {
    const { spacing } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [ContentComponent, setContentComponent] =
        useState<React.ComponentType<ModalContentProps> | null>(null);
    const [componentProps, setComponentProps] = useState<Record<string, unknown> | undefined>(
        undefined
    );
    const [fullScreenOnMobile, setFullScreenOnMobile] = useState(false);

    const { isMobile } = useResponsive();

    const handleOpenModal = useCallback(
        <T extends ModalContentProps = ModalContentProps>(options: {
            content: React.ComponentType<T>;
            onClose?: () => void;
            props?: Record<string, unknown>;
            fullScreenOnMobile?: boolean;
        }) => {
            const {
                content: contentComponent,
                props: additionalProps,
                fullScreenOnMobile: fullScreen,
            } = options;

            setContentComponent(() => contentComponent as React.ComponentType<ModalContentProps>);
            setComponentProps(additionalProps);
            setFullScreenOnMobile(fullScreen ?? false);
            setIsOpen(true);
        },
        []
    );

    const handleCloseModal = useCallback(() => {
        setIsOpen(false);
        setContentComponent(null);
        setComponentProps(undefined);
        setFullScreenOnMobile(false);
    }, []);

    const contextValue = useMemo(
        () => ({
            openModal: handleOpenModal,
            closeModal: handleCloseModal,
            isOpen,
        }),
        [isOpen, handleOpenModal, handleCloseModal]
    );

    const renderContent = () => {
        if (!ContentComponent) return null;

        return <ContentComponent {...componentProps} onClose={handleCloseModal} />;
    };

    return (
        <ModalContext.Provider value={contextValue}>
            {children}

            {/* Mobile: Drawer (when not fullScreen) */}
            {isMobile && !fullScreenOnMobile && (
                <Drawer
                    anchor="bottom"
                    open={isOpen}
                    onClose={handleCloseModal}
                    slotProps={{
                        paper: {
                            sx: {
                                width: '100%',
                                maxWidth: '100%',
                                borderRadius: `${spacing(2.5)} ${spacing(2.5)} 0 0`,
                            },
                        },
                    }}
                >
                    {renderContent()}
                </Drawer>
            )}

            {/* Dialog: Mobile (fullScreen) or Tablet/Desktop */}
            {(!isMobile || fullScreenOnMobile) && (
                <Dialog
                    open={isOpen}
                    onClose={handleCloseModal}
                    fullScreen={isMobile && fullScreenOnMobile}
                    slotProps={{
                        paper: {
                            sx: {
                                ...(isMobile && fullScreenOnMobile
                                    ? {
                                          borderRadius: 0,
                                          p: 0,
                                      }
                                    : {
                                          maxHeight: '100dvh',
                                          height: 'auto',
                                          overflow: 'hidden',
                                          display: 'grid',
                                          gridTemplateAreas: "'header' 'body' 'footer'",
                                          gridTemplateRows: 'max-content 1fr max-content',
                                          gridTemplateColumns: '1fr',
                                          p: 0,
                                      }),
                            },
                        },
                    }}
                >
                    {renderContent()}
                </Dialog>
            )}
        </ModalContext.Provider>
    );
};

export function useModalManager() {
    const context = React.useContext(ModalContext);
    if (context === undefined) {
        throw new Error('useModalManager must be used within a ModalManager');
    }
    return context;
}

export default ModalManager;
